import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import { Marker } from 'maplibre-gl';
import { useAppConfig } from '@/config/AppConfigContext';
import { useClient } from '@/clients/useClient';
import {
    initGooglePlaces,
    getSuggestions,
    getPlaceDetails,
    getPoisSuggestions,
    getDmaSuggestions,
} from '@/api/searchApi';
import type { GooglePlacesServices, SearchResult, SearchSuggestion } from '@/api/searchApi';
import type { PoiFeatureProperties } from '@/components/map/utils/poiFeatureProperties';
import { mapSearchPoiToFeatureProperties } from '@/components/map/utils/poiFeatureProperties';
import { calculateSemanticZoomDuration } from '@/components/map/utils/mapAnimation';
import { RECORD_FOCUS_ZOOM } from '@/components/map/utils/mapDefaults';

const DEBOUNCE_MS = 350;
const MIN_POI_QUERY_LENGTH = 1; // stop numbers can be a single digit
const MIN_GOOGLE_QUERY_LENGTH = 3; // keep Google Places quiet on short inputs
const SEARCH_ZOOM = RECORD_FOCUS_ZOOM;

export interface UseMapSearchReturn {
    query: string;
    setQuery: (q: string) => void;
    suggestions: SearchSuggestion[];
    isLoading: boolean;
    isReady: boolean;
    selectedResult: SearchResult | null;
    handleSelect: (suggestion: SearchSuggestion) => Promise<void>;
    clearSearch: () => void;
}

function createSearchMarkerElement(): HTMLElement {
    const el = document.createElement('div');
    el.style.width = '104px';
    el.style.height = '144px';
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="104" height="144" viewBox="0 0 104 144">
  <circle cx="52" cy="77" r="50" fill="#007CFF" opacity="0.2"/>
  <circle cx="52" cy="77" r="29" fill="#007CFF" opacity="0.35"/>
  <g transform="translate(30,30)">
    <path d="M22 1C11.5 1 1 11.5 1 22C1 31 6 38 14 46L22 54L30 46C38 38 43 31 43 22C43 11.5 32.5 1 22 1Z" fill="white"/>
    <circle cx="22" cy="22" r="17" fill="#007CFF"/>
    <path d="M22 11C17 17 15 23 15 27A7 7 0 0 0 29 27C29 23 27 17 22 11Z" fill="white"/>
  </g>
</svg>`;
    return el;
}

export function useMapSearch(
    mapRef: RefObject<MapRef | null>,
    onPoiSelect?: (poi: PoiFeatureProperties, coords: [number, number]) => void,
    onDmaSelect?: (ident: string) => void,
    /**
     * Fired by {@link UseMapSearchReturn.clearSearch}. Clearing releases only what search
     * created (the marker here, a search-originated highlight via this callback) — it never
     * clears a selection: selections carry no provenance, so nothing may key off "search
     * selected this" after the fact.
     */
    onSearchCleared?: () => void,
): UseMapSearchReturn {
    const { googleMapsApiKey } = useAppConfig();
    const { selectedClient } = useClient();
    // isReady reflects ONLY Google Places (address autocomplete) availability. POI/DMA
    // search work without it, so it must not gate the search button — see MapSearchBox.
    const [isReady, setIsReady] = useState(false);
    const [queryState, setQueryState] = useState('');
    const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedResult, setSelectedResult] = useState<SearchResult | null>(null);

    const servicesRef = useRef<GooglePlacesServices | null>(null);
    const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
    const activeRequestRef = useRef(0);
    const markerRef = useRef<Marker | null>(null);
    const onPoiSelectRef = useRef(onPoiSelect);
    const onDmaSelectRef = useRef(onDmaSelect);
    const onSearchClearedRef = useRef(onSearchCleared);
    useEffect(() => {
        onPoiSelectRef.current = onPoiSelect;
        onDmaSelectRef.current = onDmaSelect;
        onSearchClearedRef.current = onSearchCleared;
    });

    useEffect(() => {
        if (!googleMapsApiKey) return;

        let cancelled = false;
        void initGooglePlaces(googleMapsApiKey).then((services) => {
            if (cancelled || !services) return;
            servicesRef.current = services;
            sessionTokenRef.current = services.newSessionToken();
            setIsReady(true);
        });

        return () => {
            cancelled = true;
        };
    }, [googleMapsApiKey]);

    useEffect(() => {
        return () => {
            // A pending debounce must die with the hook — letting it fire would fetch
            // suggestions for a search box that no longer exists.
            clearTimeout(debounceTimerRef.current);
            markerRef.current?.remove();
        };
    }, []);

    const setQuery = useCallback(
        (value: string) => {
            setQueryState(value);
            clearTimeout(debounceTimerRef.current);

            if (value.length < MIN_POI_QUERY_LENGTH) {
                setSuggestions([]);
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            const requestId = ++activeRequestRef.current;
            const clientId = selectedClient?.id ?? null;
            const focalPoint = selectedClient?.focalPoint ?? null;
            const languageCode = selectedClient?.languageCode;

            debounceTimerRef.current = setTimeout(() => {
                const fetches: Promise<SearchSuggestion[]>[] = [];

                if (clientId) {
                    fetches.push(getPoisSuggestions(value, clientId));
                    fetches.push(getDmaSuggestions(value, clientId));
                }

                if (value.length >= MIN_GOOGLE_QUERY_LENGTH && servicesRef.current && sessionTokenRef.current) {
                    // Bias (never restrict) address suggestions to where the user is looking:
                    // the live viewport first, the client's focal point before the map exists,
                    // nothing when neither is available. Read at fire time so a pan during the
                    // debounce is reflected.
                    let locationBias: google.maps.places.LocationBias | undefined;
                    const map = mapRef.current?.getMap();
                    if (map) {
                        const bounds = map.getBounds();
                        locationBias = {
                            north: bounds.getNorth(),
                            south: bounds.getSouth(),
                            east: bounds.getEast(),
                            west: bounds.getWest(),
                        };
                    } else if (focalPoint) {
                        locationBias = { lat: focalPoint.coordinates[1], lng: focalPoint.coordinates[0] };
                    }

                    fetches.push(
                        getSuggestions(value, sessionTokenRef.current, {
                            locationBias,
                            language: languageCode,
                        }).then((results) => results.map((s) => ({ ...s, source: 'address' as const }))),
                    );
                }

                if (fetches.length === 0) {
                    setSuggestions([]);
                    setIsLoading(false);
                    return;
                }

                void Promise.all(fetches).then((results) => {
                    if (requestId !== activeRequestRef.current) return;
                    // POI → DMA → Address order
                    setSuggestions(results.flat());
                    setIsLoading(false);
                });
            }, DEBOUNCE_MS);
        },
        // selectedClient changes trigger a new closure for the correct client id,
        // focal-point fallback and language; mapRef is a stable ref, read at debounce-fire time

        [selectedClient?.id, selectedClient?.focalPoint, selectedClient?.languageCode, mapRef],
    );

    const handleSelect = useCallback(
        async (suggestion: SearchSuggestion) => {
            setSuggestions([]);
            setIsLoading(true);
            ++activeRequestRef.current;

            if (suggestion.source === 'dma' && suggestion.dmaData) {
                const dma = suggestion.dmaData;
                setIsLoading(false);
                markerRef.current?.remove();
                markerRef.current = null;

                onDmaSelectRef.current?.(dma.ident);

                const map = mapRef.current?.getMap();
                if (map) {
                    map.fitBounds(
                        [
                            [dma.minLng, dma.minLat],
                            [dma.maxLng, dma.maxLat],
                        ],
                        { padding: 80, duration: 600 },
                    );
                }
                return;
            }

            // POI and leak picks share one flow: the feature-properties producer carries the
            // entity discriminator, and the caller's onPoiSelect routes to the right panel.
            if ((suggestion.source === 'poi' || suggestion.source === 'leak') && suggestion.poiData) {
                const poi = suggestion.poiData;
                const coords: [number, number] = [poi.longitude, poi.latitude];

                setIsLoading(false);
                markerRef.current?.remove();
                markerRef.current = null;

                onPoiSelectRef.current?.(mapSearchPoiToFeatureProperties(poi), coords);

                const map = mapRef.current?.getMap();
                if (map) {
                    const currentZoom = map.getZoom();
                    map.easeTo({
                        center: coords,
                        zoom: SEARCH_ZOOM,
                        duration: calculateSemanticZoomDuration(currentZoom, SEARCH_ZOOM),
                    });
                }
                return;
            }

            // Address / geocoder path
            if (!servicesRef.current || !sessionTokenRef.current) {
                setIsLoading(false);
                return;
            }

            const result = await getPlaceDetails(suggestion.id, suggestion.placePrediction);

            sessionTokenRef.current = servicesRef.current.newSessionToken();
            setIsLoading(false);

            if (!result) return;

            setSelectedResult(result);

            const map = mapRef.current?.getMap();
            if (!map) return;

            markerRef.current?.remove();
            markerRef.current = new Marker({
                element: createSearchMarkerElement(),
                anchor: 'center',
                offset: [0, -12],
            })
                .setLngLat(result.coordinates)
                .addTo(map);

            if (result.bbox) {
                map.fitBounds(
                    [
                        [result.bbox[0], result.bbox[1]],
                        [result.bbox[2], result.bbox[3]],
                    ],
                    { padding: 64, duration: 600 },
                );
            } else {
                const currentZoom = map.getZoom();
                map.easeTo({
                    center: result.coordinates,
                    zoom: SEARCH_ZOOM,
                    duration: calculateSemanticZoomDuration(currentZoom, SEARCH_ZOOM),
                });
            }
        },
        [mapRef],
    );

    const clearSearch = useCallback(() => {
        clearTimeout(debounceTimerRef.current);
        // The set-state-in-effect suppressions cover the ONE effect caller (the client-switch
        // effect below, guarded to fire only on a genuine switch); every other caller is an
        // event handler, where these resets are the normal pattern.
        // eslint-disable-next-line react-x/set-state-in-effect
        setQueryState('');
        // eslint-disable-next-line react-x/set-state-in-effect
        setSuggestions([]);
        // eslint-disable-next-line react-x/set-state-in-effect
        setIsLoading(false);
        // eslint-disable-next-line react-x/set-state-in-effect
        setSelectedResult(null);
        markerRef.current?.remove();
        markerRef.current = null;
        onSearchClearedRef.current?.();
    }, []);

    // A client switch invalidates everything search produced for the old client — the query,
    // suggestions, a selected address result and its map marker. A "switch" is a change
    // AWAY from a resolved id: the bootstrap resolve (null → first id) must not wipe a query
    // typed while the client list was still loading. Interaction-state resets (selection,
    // highlights) are the provider's CLIENT_CHANGED, not this effect's, job.
    const prevClientIdRef = useRef<string | null>(null);
    useEffect(() => {
        const clientId = selectedClient?.id ?? null;
        const prev = prevClientIdRef.current;
        prevClientIdRef.current = clientId;
        if (prev === null || prev === clientId) return;
        clearSearch();
    }, [selectedClient?.id, clearSearch]);

    return {
        query: queryState,
        setQuery,
        suggestions,
        isLoading,
        isReady,
        selectedResult,
        handleSelect,
        clearSearch,
    };
}
